import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CCard, CCardBody, CCardHeader, CCol, CRow, CButton, CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody } from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useLocation } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilSearch } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { FaBell } from 'react-icons/fa';
import debounce from 'lodash.debounce';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import { useConvertToLocalTime } from 'src/components/store/useConvertToLocalTime';
import AsyncSelect from 'react-select/async';
import PermissionsNoPage, { Permissions } from 'src/components/Permissions';

const page = 1;
const urlApi = '/massive_notifications';
const urlRouter = '/massive-notifications';

export async function LoaderMassiveNotifications() {
  const res = await AxiosInstance.get(`${urlApi}?page=${page}`).then((response) => response);
  return res;
}

const MassiveNotifications = ({ user_data }) => {
  // if (!Permissions(user_data, 'MassNotifications', 'VIEW_MASSNOTIFICATIONS')) {
  //   return <PermissionsNoPage />;
  // }

  const { data } = useLoaderData();
  const location = useLocation();
  const toastShownRef = useRef(false);
  const [rows, setRows] = useState(data['hydra:member'] || []);
  const [rowCountState, setRowCountState] = useState(data['hydra:totalItems'] || 0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    const toastState = location.state?.toast;
    if (toastState && !toastShownRef.current) {
      toastShownRef.current = true;
      const { type = 'success', message } = toastState;
      toast[type](message);
    }
  }, [location]);

  useEffect(() => {
    setRows(data['hydra:member'] || []);
    setRowCountState(data['hydra:totalItems'] || 0);
  }, [data]);

  const getRowsTable = useCallback((p = 1, userIdParam) => {
    const userId = userIdParam !== undefined ? userIdParam : selectedUserId;
    const userQuery = userId ? `&userCreated.id=${encodeURIComponent(userId)}` : '';

    AxiosInstance.get(`${urlApi}?page=${p}${userQuery}`).then((response) => {
      setRows(response.data['hydra:member']);
      setRowCountState(response.data['hydra:totalItems']);
    });
  }, [selectedUserId]);

  const fetchUserOptions = useCallback(async (inputValue) => {
    const query = inputValue.trim();
    if (!query || query.length < 3) {
      return [];
    }

    const response = await AxiosInstance.get(`/user/search?s=${encodeURIComponent(query)}`);
    return response.data.map((user) => {
      const name = user.firstName ? ` (${user.firstName})` : '';
      return {
        label: `${user.username}${name}`,
        value: user.id,
      };
    });
  }, []);

  const debouncedLoadUserOptions = useMemo(() => debounce((inputValue, resolve, reject) => {
    fetchUserOptions(inputValue).then(resolve).catch(reject);
  }, 300), [fetchUserOptions]);

  useEffect(() => {
    return () => debouncedLoadUserOptions.cancel();
  }, [debouncedLoadUserOptions]);

  const loadUserOptions = useCallback((inputValue) => {
    return new Promise((resolve, reject) => debouncedLoadUserOptions(inputValue, resolve, reject));
  }, [debouncedLoadUserOptions]);

  const handleUserChange = (option) => {
    const userId = option ? option.value : null;
    setSelectedUser(option);
    setSelectedUserId(userId);
    getRowsTable(1, userId);
  };

  const handlePageChange = (p) => {
    getRowsTable(p + 1);
  };

  const handleClearFilters = () => {
    setSelectedUser(null);
    setSelectedUserId(null);
    getRowsTable(1, null);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'name', headerName: 'Назва', width: 360 },
    { field: 'title', headerName: 'Тема', width: 240 },
    { field: 'body', headerName: 'Текст', width: 240 },
    {
      field: 'userCreated',
      headerName: 'Створив користувач',
      width: 200,
      renderCell: (params) => params.row.userCreated?.username || '',
    },
    { field: 'status', headerName: 'Статус', width: 120 },
    // {
    //   field: 'dateEntered',
    //   headerName: 'Дата створення',
    //   width: 200,
    //   renderCell: (params) => <span>{useConvertToLocalTime(params.row.dateEntered)}</span>,
    // },
    {
      field: 'action',
      headerName: 'Переглянути',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      width: 110,
      renderCell: (params) => (
        <Link
          to={`${urlRouter}/view/${params.row.id}`}
          className='cutom-view-icon '
        >
          <CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
        </Link>
      ),
    },
  ];

  return (
    <CRow>
      <CCol xs={12}>
        <ToastContainer />
        <div className="d-flex justify-content-start mb-3">
          <Link to={`/massive-notifications/create`}>
            <CButton color="secondary">Створити сповіщення <FaBell className="nav-icon" /></CButton>
          </Link>
        </div>
        <CCard className="mb-4">
          <CAccordion flush>
            <CAccordionItem>
              <CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
              <CAccordionBody>
                <CRow className="my-3 mx-2">
                  <CCol md={8}>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={false}
                      loadOptions={loadUserOptions}
                      value={selectedUser}
                      onChange={handleUserChange}
                      isClearable
                      placeholder="Створив користувач"
                    />
                  </CCol>
                  <CCol md={4}>
                    <CButton color="secondary" variant="outline" onClick={handleClearFilters}>
                      Очистити фільтри
                    </CButton>
                  </CCol>
                </CRow>
              </CAccordionBody>
            </CAccordionItem>
          </CAccordion>
          <CCardHeader>
            <strong><FaBell className="nav-icon" /> Масові сповіщення</strong>
          </CCardHeader>
          <CCardBody>
            <div style={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={30}
                rowsPerPageOptions={[30]}
                pagination
                rowCount={rowCountState}
                paginationMode="server"
                onPageChange={handlePageChange}
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default MassiveNotifications;

